//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useCallback, useRef } from 'react';
import UsersPanel, { UserLoopsData } from './UsersPanel';
import { useTranslation } from 'react-i18next';
import TenantDataService from '../../utils/TenantDataService';
import LoopService from '../../utils/LoopDataService';
import { mapWithConcurrency } from '../../utils/concurrency';
import LicenseService from '../../utils/LicenseDataService';
import { useUserAuth } from '../../UserAuth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface TenantData {
  name: string;
  id: string;
  createdOn: string;
  subscriptionId: string;
  subscriptionUserAccount: string;
  users: number;
  licenses: number;
  teamSpaces: number;
  loops: number;
  /** Usernames in this tenant; input for the background loop fan-out. */
  usernames?: string[];
}

interface Tenant {
  name: string;
  id: string;
  creationDate?: string;
}
interface User {
  id: string;
  username: string;
  email: string;
  userid: string;
  firstname: string | null;
  lastname: string | null;
}

interface UsersResponse {
  _embedded?: {
    users?: User[];
  };
}

interface LicensesResponse {
  selectedTier: string;
  features: {
    [key: string]: {
      total: number;
      leased: number;
      userIds: string[];
    };
  };
  mode?: string;
}

interface LicenseInfo {
  tier: string;
  used: number;
  total: number;
  remaining: number;
  color: string;
}

interface LicenseTierData {
  selectedTier: string;
  tiers: LicenseInfo[];
  mode?: string;
}
interface LoopResponse {
  _embedded?: {
    loops?: any[];
  };
  page?: {
    totalElements: number;
  };
}

/**
 * Max in-flight GET /loops?userId=... requests handed to the loop service.
 *
 * NOTE: this is NOT the real throughput limiter. LoopDataService wraps every
 * request in an `async.queue` with concurrency 3 (see its constructor), and the
 * layout uses a single service instance, so at most 3 loop requests are ever on
 * the wire regardless of this value. It is set to match so the pool does not
 * pile work into that queue. To speed up the fan-out, the queue limit in
 * LoopDataService must be raised (it applies to every loop request in the app,
 * so agree that with the backend team first) and this value raised with it.
 */
const LOOP_FETCH_CONCURRENCY = 3;
/** Minimum gap between progressive state publishes during the fan-out. */
const LOOP_PUBLISH_INTERVAL_MS = 750;

const EMPTY_LOOP_DATA: UserLoopsData = {
  byUser: {},
  loopNames: [],
  loopTeamspaceIds: {},
  loading: false,
};

const UserAdminLayout: React.FC = () => {
  const { user, isAuthenticated } = useUserAuth();
  const tenantDataService = useRef(
    new TenantDataService(
      () => user,
      () => isAuthenticated,
    ),
  ).current;
  const loopService = useRef(
    new LoopService(
      () => user,
      () => isAuthenticated,
    ),
  ).current;
  const licenseService = useRef(
    new LicenseService(
      () => user,
      () => isAuthenticated,
    ),
  ).current;
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [rawTenants, setRawTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<TenantData | null>(null);
  const [tenantData, setTenantData] = useState<TenantData>({
    name: '',
    id: '',
    createdOn: '',
    subscriptionId: '',
    subscriptionUserAccount: '',
    users: 0,
    licenses: 0,
    teamSpaces: 0,
    loops: 0,
  });
  const [licenseData, setLicenseData] = useState<LicenseTierData>({
    selectedTier: '',
    tiers: [],
  });
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Loop memberships for the selected tenant, filled in by a background fan-out
  // AFTER the page has rendered. Shared with UsersPanel so it is fetched once.
  const [loopData, setLoopData] = useState<UserLoopsData>(EMPTY_LOOP_DATA);
  // Identifies the current fan-out so a superseded run (tenant switch, refresh,
  // unmount) stops scheduling requests and never publishes stale results.
  const loopRunIdRef = useRef(0);
  const unmountedRef = useRef(false);
  const selectedUsernamesRef = useRef<string[]>([]);

  const convertToTenantData = async (tenant: Tenant): Promise<TenantData> => {
    let users: User[] = [];
    let licenseCount = 0;

    try {
      const usersResponse = (await Promise.resolve(
        tenantDataService.getAllUsers(),
      )) as UsersResponse;
      users = usersResponse?._embedded?.users || [];

      try {
        const licensesResponse =
          (await licenseService.getLicenses()) as LicensesResponse;
        if (licensesResponse?.features) {
          // Sum totals across all nexus tiers (exclude 'none') rather than only selectedTier
          licenseCount = Object.entries(licensesResponse.features)
            .filter(([tierKey]) => tierKey.startsWith('nexus-t'))
            .reduce((sum, [, tierData]) => sum + (tierData.total || 0), 0);
        }
      } catch (licenseError) {
        // Ignore license errors; leave licenseCount at 0
      }
      let teamSpaceCount = 0;
      try {
        const teamspacesCountResponse =
          (await tenantDataService.getTeamspacesCount(tenant.id)) as any;
        teamSpaceCount =
          typeof teamspacesCountResponse === 'number'
            ? teamspacesCountResponse
            : 0;
      } catch (teamspaceError) {
        teamSpaceCount = 0;
      }

      // Loops are deliberately NOT counted here. That requires one GET /loops
      // request per user (the server restricts unfiltered listing to the admin's
      // own memberships), which for large tenants used to hold the whole page on
      // the spinner for ~45s. loadLoopsForUsers() does it in the background once
      // the page is visible and streams the count into the Loops card.
      const result: TenantData = {
        name: tenant.name,
        id: tenant.id,
        createdOn: tenant.creationDate
          ? new Date(tenant.creationDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })
          : '',
        subscriptionId: tenant.id,
        subscriptionUserAccount: tenant.name,
        users: users.length,
        licenses: licenseCount,
        teamSpaces: teamSpaceCount,
        loops: 0,
        usernames: users.map((u) => u.username).filter((n) => !!n),
      };

      return result;
    } catch (error) {
      console.error(`Error fetching data for tenant ${tenant.id}:`, error);

      return {
        name: tenant.name,
        id: tenant.id,
        createdOn: tenant.creationDate
          ? new Date(tenant.creationDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })
          : '',
        subscriptionId: tenant.id,
        subscriptionUserAccount: tenant.name,
        users: 0,
        licenses: 0,
        teamSpaces: 0,
        loops: 0,
        usernames: [],
      };
    }
  };

  /**
   * Background fan-out: one GET /loops?userId=<username> per user, through a
   * sliding-window pool, publishing partial results every LOOP_PUBLISH_INTERVAL_MS
   * so the Loops card and the per-user loop badges fill in while it runs.
   * This is the ONLY place loop data is fetched for the page; UsersPanel reads
   * it via props instead of repeating the same requests.
   *
   * @param usernames - users to query
   * @param keepPrevious - true on a manual refresh: keep the currently displayed
   *   count/memberships on screen (only flag them as loading) and swap in the
   *   new results once at the end, instead of resetting to 0/empty and
   *   re-streaming. False on initial load and tenant switch, where the old data
   *   is not for this user set and progressive streaming is what we want.
   */
  const loadLoopsForUsers = useCallback(
    async (usernames: string[], keepPrevious = false) => {
      const runId = ++loopRunIdRef.current;
      const isStale = () =>
        runId !== loopRunIdRef.current || unmountedRef.current;

      const byUser: Record<string, string[]> = {};
      // loop id -> display name (or undefined); distinct ids give the count,
      // distinct truthy names give the filter dropdown list.
      const loopNameById = new Map<string, string | undefined>();
      // loop display name -> teamSpaceId (from the Loop Server response).
      const loopTeamspaceIds: Record<string, string> = {};
      let lastPublish = 0;

      const publish = (loading: boolean) => {
        if (isStale()) return;
        lastPublish = Date.now();
        const loopNames = Array.from(
          new Set(
            Array.from(loopNameById.values()).filter((n): n is string => !!n),
          ),
        );
        setLoopData({
          byUser: { ...byUser },
          loopNames,
          loopTeamspaceIds: { ...loopTeamspaceIds },
          loading,
        });
        setTenantData((prev) => ({ ...prev, loops: loopNameById.size }));
      };

      if (keepPrevious) {
        // Leave the visible data alone; just show that a refresh is running.
        setLoopData((prev) => ({ ...prev, loading: true }));
      } else {
        publish(true);
      }
      if (usernames.length === 0) {
        publish(false);
        return;
      }

      await mapWithConcurrency(
        usernames,
        LOOP_FETCH_CONCURRENCY,
        async (username) => {
          const names: string[] = [];
          try {
            const resp = (await loopService.getLoopCountUser(
              true,
              username,
            )) as LoopResponse;
            for (const loop of resp?._embedded?.loops || []) {
              const name: string | undefined = loop?.displayName || loop?.name;
              if (loop?.id) loopNameById.set(loop.id, name);
              if (name) {
                names.push(name);
                if (loop?.teamSpaceId) {
                  loopTeamspaceIds[name] = loop.teamSpaceId;
                }
              }
            }
          } catch {
            // That user simply contributes no loops; keep going.
          }
          byUser[username.toLowerCase()] = names;
          // Progressive publishes only when we started from empty; during a
          // refresh a partial set would look like loops disappearing.
          if (
            !keepPrevious &&
            Date.now() - lastPublish >= LOOP_PUBLISH_INTERVAL_MS
          ) {
            publish(true);
          }
        },
        { shouldStop: isStale },
      );

      publish(false);
    },
    [loopService],
  );

  const startLoopFanOut = useCallback(
    (usernames: string[] | undefined, keepPrevious = false) => {
      selectedUsernamesRef.current = usernames || [];
      // Intentionally not awaited: the page must render while this runs.
      void loadLoopsForUsers(selectedUsernamesRef.current, keepPrevious);
    },
    [loadLoopsForUsers],
  );

  /** Stops any running fan-out from scheduling more requests or publishing. */
  const abandonLoopFanOut = useCallback(() => {
    unmountedRef.current = true;
    loopRunIdRef.current++;
  }, []);

  const fetchLicenseData = useCallback(async () => {
    try {
      const response = (await licenseService.getLicenses()) as LicensesResponse;

      if (response && response.features) {
        const tiers: LicenseInfo[] = [];
        const tierColors = {
          'nexus-t1': '#3b82f6',
          'nexus-t2': '#10b981',
          'nexus-t3': '#8b5cf6',
        };

        Object.keys(response.features).forEach((tierKey) => {
          if (tierKey.toLowerCase() === 'none') return;
          const tierData = response.features[tierKey];
          const displayName = tierKey.replace('nexus-t', 'Tier');
          const color =
            tierColors[tierKey as keyof typeof tierColors] || '#3b82f6';

          tiers.push({
            tier: displayName,
            used: tierData.leased,
            total: tierData.total,
            remaining: tierData.total - tierData.leased,
            color: color,
          });
        });

        const selectedDisplayTier = response.selectedTier.replace(
          'nexus-t',
          'Tier',
        );

        // This ensures React detects the change and re-renders LicenseDisplay
        setLicenseData({
          selectedTier: selectedDisplayTier,
          tiers: tiers.map((t) => ({ ...t })), // Deep copy each tier object
          mode: response.mode,
        });
      }
    } catch (error) {
      console.error('Error fetching license data:', error);
    }
  }, [licenseService]);

  useEffect(() => {
    let cancelled = false;
    // Reset on (re)mount: React StrictMode runs effect cleanup once in dev
    // before the real mount, and the flag must not stay stuck at true.
    unmountedRef.current = false;

    const fetchTenants = async () => {
      try {
        const response = (await tenantDataService.getAllTenants()) as Tenant[];
        if (response && response.length > 0) {
          const uniqueTenants = Array.from(
            new Map(response.map((tenant) => [tenant.id, tenant])).values(),
          );
          const convertedTenantsPromises =
            uniqueTenants.map(convertToTenantData);
          const convertedTenants = await Promise.all(convertedTenantsPromises);
          if (!cancelled) {
            setRawTenants(uniqueTenants);
            setTenants(convertedTenants);
            const firstTenant = convertedTenants[0];
            setSelectedTenant(firstTenant);
            setTenantData(firstTenant);
            startLoopFanOut(firstTenant.usernames);
          }
        } else if (!cancelled) {
          const emptyTenantData: TenantData = {
            id: '',
            name: '',
            createdOn: '',
            subscriptionId: '',
            subscriptionUserAccount: '',
            users: 0,
            licenses: 0,
            teamSpaces: 0,
            loops: 0,
          };
          setRawTenants([]);
          setTenants([]);
          setSelectedTenant(emptyTenantData);
          setTenantData(emptyTenantData);
        }
      } catch (error) {
        if (!cancelled) {
          const emptyTenantData: TenantData = {
            id: '',
            name: '',
            createdOn: '',
            subscriptionId: '',
            subscriptionUserAccount: '',
            users: 0,
            licenses: 0,
            teamSpaces: 0,
            loops: 0,
          };
          setRawTenants([]);
          setTenants([]);
          setSelectedTenant(emptyTenantData);
          setTenantData(emptyTenantData);
        }
      }
    };

    const run = async () => {
      setIsLoading(true);
      await Promise.all([fetchTenants(), fetchLicenseData()]);
      if (!cancelled) setIsLoading(false);
    };

    run();
    return () => {
      cancelled = true;
      // Stop the background loop fan-out from scheduling further requests and
      // from publishing into an unmounted component.
      abandonLoopFanOut();
    };
  }, []);

  const handleTenantSelect = async (tenant: Tenant) => {
    const convertedTenant = await convertToTenantData(tenant);
    setSelectedTenant(convertedTenant);
    setTenantData(convertedTenant);
    startLoopFanOut(convertedTenant.usernames);
  };

  /**
   * Manual refresh from UsersPanel. The panel has just re-fetched the user list,
   * so it passes the fresh usernames: users added since page load get their
   * loops fetched, removed users stop being queried. Falls back to the last
   * known list if the panel could not provide one.
   */
  const handleRefreshLoops = useCallback(
    (freshUsernames?: string[]) => {
      const usernames = Array.isArray(freshUsernames)
        ? freshUsernames
        : selectedUsernamesRef.current;
      startLoopFanOut(usernames, true);
    },
    [startLoopFanOut],
  );

  if (isLoading) {
    return (
      <div
        id="id-user-admin-loading-container"
        data-testid="user-admin-loading-container"
        className="flex h-[calc(100vh-70px)] w-full flex-col items-center justify-center"
      >
        <div
          id="id-user-admin-spinner"
          data-testid="user-admin-spinner"
          className="size-12 animate-spin rounded-full border-4 border-gray-200 border-t-brand-blue"
        />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('loading-trans')}
        </p>
      </div>
    );
  }

  return (
    <div
      id="id-user-admin-root"
      data-testid="user-admin-root"
      className="min-h-screen w-full bg-carbon-light dark:bg-carbon-dark"
    >
      {/* <header className="flex h-16 w-full items-center bg-white px-4 dark:bg-[#1f1f1f]">
        <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          {t('user-administration')}
        </h1>
      </header> */}

      <div
        id="id-user-admin-content"
        data-testid="user-admin-content"
        className="px-4 pb-6 pt-4"
      >
        <div
          id="id-user-admin-panel"
          data-testid="user-admin-panel"
          className="rounded-lg bg-white p-6 dark:bg-carbon-darkPanel"
        >
          {/* Manage heading + Switch Tenant row */}
          <div
            id="id-user-admin-heading-row"
            data-testid="user-admin-heading-row"
            className="mb-6 flex items-center justify-between"
          >
            <h2 className="text-base font-medium text-carbon-textPrimary dark:text-white">
              {tenantData.name
                ? t('manage-tenant-users', { name: tenantData.name })
                : t('user-administration')}
            </h2>
            {rawTenants.length > 1 && (
              <div
                id="id-user-admin-tenant-switch"
                data-testid="user-admin-tenant-switch"
                className="flex items-center gap-2"
              >
                <span
                  id="id-user-admin-switch-label"
                  data-testid="user-admin-switch-label"
                  className="text-sm text-carbon-textSecondary dark:text-gray-400"
                >
                  {t('switch-tenant')}:
                </span>
                <Select
                  value={selectedTenant?.id || ''}
                  onValueChange={(value) => {
                    const found = rawTenants.find((ten) => ten.id === value);
                    if (found) handleTenantSelect(found);
                  }}
                >
                  <SelectTrigger
                    id="id-switch-tenant-select"
                    data-testid="switch-tenant-select"
                    className="h-8 w-auto min-w-[120px] border-gray-300 bg-white text-sm text-gray-700 dark:border-gray-600 dark:bg-carbon-darkPanel dark:text-gray-300"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rawTenants.map((ten) => (
                      <SelectItem key={ten.id} value={ten.id}>
                        {ten.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Tenant Info Cards */}
          <div
            id="id-user-admin-tenant-info"
            data-testid="user-admin-tenant-info"
            className="mb-6"
          >
            <div
              id="id-user-admin-info-cards"
              data-testid="user-admin-info-cards"
              className="flex gap-3"
            >
              <div
                id="id-user-admin-card-tenant-name"
                data-testid="user-admin-card-tenant-name"
                className="rounded bg-carbon-surfaceSecondary p-3 dark:bg-carbon-textPrimary"
              >
                <div
                  id="id-user-admin-label-tenant-name"
                  data-testid="user-admin-label-tenant-name"
                  className="mb-0.5 text-xs text-carbon-textSecondary dark:text-gray-400"
                >
                  {t('tenant-name')}
                </div>
                <div
                  id="id-user-admin-value-tenant-name"
                  data-testid="user-admin-value-tenant-name"
                  className="whitespace-nowrap text-sm font-medium text-carbon-textPrimary dark:text-white"
                >
                  {tenantData.name}
                </div>
              </div>
              <div
                data-testid="user-admin-card-tenant-id"
                className="rounded bg-carbon-surfaceSecondary p-3 dark:bg-carbon-textPrimary"
              >
                <div
                  data-testid="user-admin-label-tenant-id"
                  className="mb-0.5 text-xs text-carbon-textSecondary dark:text-gray-400"
                >
                  {t('tenant-id')}
                </div>
                <div
                  data-testid="user-admin-value-tenant-id"
                  className="whitespace-nowrap text-sm font-medium text-carbon-textPrimary dark:text-white"
                >
                  {tenantData.id}
                </div>
              </div>
              <div
                id="id-user-admin-card-created-on"
                data-testid="user-admin-card-created-on"
                className="rounded bg-carbon-surfaceSecondary p-3 dark:bg-carbon-textPrimary"
              >
                <div
                  id="id-user-admin-label-created-on"
                  data-testid="user-admin-label-created-on"
                  className="mb-0.5 text-xs text-carbon-textSecondary dark:text-gray-400"
                >
                  {t('created-on')}
                </div>
                <div
                  id="id-user-admin-value-created-on"
                  data-testid="user-admin-value-created-on"
                  className="whitespace-nowrap text-sm font-medium text-carbon-textPrimary dark:text-white"
                >
                  {tenantData.createdOn}
                </div>
              </div>
              <div
                id="id-user-admin-card-team-spaces"
                data-testid="user-admin-card-team-spaces"
                className="rounded bg-carbon-surfaceSecondary p-3 text-center dark:bg-carbon-textPrimary"
              >
                <div
                  id="id-user-admin-label-team-spaces"
                  data-testid="user-admin-label-team-spaces"
                  className="mb-0.5 text-xs text-carbon-textSecondary dark:text-gray-400"
                >
                  {t('team-spaces')}
                </div>
                <div
                  id="id-user-admin-value-team-spaces"
                  data-testid="user-admin-value-team-spaces"
                  className="text-xl font-semibold text-brand-nav dark:text-blue-400"
                >
                  {tenantData.teamSpaces}
                </div>
              </div>
              <div
                id="id-user-admin-card-loops"
                data-testid="user-admin-card-loops"
                className="rounded bg-carbon-surfaceSecondary p-3 text-center dark:bg-carbon-textPrimary"
              >
                <div
                  id="id-user-admin-label-loops"
                  data-testid="user-admin-label-loops"
                  className="mb-0.5 text-xs text-carbon-textSecondary dark:text-gray-400"
                >
                  {t('loops')}
                </div>
                <div
                  id="id-user-admin-value-loops"
                  data-testid="user-admin-value-loops"
                  className="text-xl font-semibold text-brand-nav dark:text-blue-400"
                >
                  {tenantData.loops}
                  {loopData.loading && (
                    <span
                      id="id-user-admin-loops-loading"
                      data-testid="user-admin-loops-loading"
                      role="status"
                      aria-label={t('loading-trans')}
                      className="ml-1 inline-block size-3 animate-spin rounded-full border-2 border-gray-300 border-t-brand-blue align-middle"
                    />
                  )}
                </div>
              </div>
              <div
                id="id-user-admin-card-users"
                data-testid="user-admin-card-users"
                className="rounded bg-carbon-surfaceSecondary p-3 text-center dark:bg-carbon-textPrimary"
              >
                <div
                  id="id-user-admin-label-users"
                  data-testid="user-admin-label-users"
                  className="mb-0.5 text-xs text-carbon-textSecondary dark:text-gray-400"
                >
                  {t('users')}
                </div>
                <div
                  id="id-user-admin-value-users"
                  data-testid="user-admin-value-users"
                  className="text-xl font-semibold text-brand-nav dark:text-blue-400"
                >
                  {tenantData.users}
                </div>
              </div>
            </div>
          </div>

          {/* Users panel */}
          <UsersPanel
            licenseData={licenseData}
            onLicenseChange={fetchLicenseData}
            loopData={loopData}
            onRefreshLoops={handleRefreshLoops}
          />
        </div>
      </div>
    </div>
  );
};

export default UserAdminLayout;
